import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TrendingDown, TrendingUp, History, ChevronDown } from 'lucide-react';
import { formatPrice, formatDate, getRelativeTime } from '@/lib/formatters';
import { priceTypeLabels } from '@/types/database';
import type { Tables } from '@/integrations/supabase/types';

type PriceDK = Tables<'prices_dk'>;
type PriceHistory = Tables<'price_history'>;

interface PriceWithHistory extends PriceDK {
  price_history: PriceHistory[];
}

interface PriceHistorySectionProps {
  prices: PriceWithHistory[];
}

export function PriceHistorySection({ prices }: PriceHistorySectionProps) {
  const [openPriceIds, setOpenPriceIds] = useState<Set<string>>(new Set());

  const togglePrice = (priceId: string) => {
    setOpenPriceIds(prev => {
      const next = new Set(prev);
      if (next.has(priceId)) {
        next.delete(priceId);
      } else {
        next.add(priceId);
      }
      return next;
    });
  };

  if (prices.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Priser</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {prices.map((price) => {
            const history = price.price_history || [];
            const latestChange = history.length > 0 
              ? history.sort((a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime())[0]
              : null;
            
            const priceDifference = latestChange 
              ? latestChange.new_price_dkk - latestChange.old_price_dkk 
              : 0;
            const isPriceDecrease = priceDifference < 0;
            const isPriceIncrease = priceDifference > 0;
            const hasHistory = history.length > 0;
            const isOpen = openPriceIds.has(price.id);

            return (
              <div key={price.id} className="space-y-2">
                {/* Current Price Row */}
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{priceTypeLabels[price.price_type]}</p>
                      {latestChange && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs gap-1 ${
                            isPriceDecrease 
                              ? 'text-green-600 border-green-200 bg-green-50' 
                              : 'text-destructive border-destructive/20 bg-destructive/5'
                          }`}
                        >
                          {isPriceDecrease ? (
                            <>
                              <TrendingDown className="w-3 h-3" />
                              {formatPrice(Math.abs(priceDifference))}
                            </>
                          ) : (
                            <>
                              <TrendingUp className="w-3 h-3" />
                              +{formatPrice(priceDifference)}
                            </>
                          )}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Gældende fra {formatDate(price.effective_date)}
                    </p>
                  </div>
                  <p className="font-display font-bold text-lg">
                    {formatPrice(price.price_dkk)}
                  </p>
                </div>

                {/* Price History Collapsible */}
                {hasHistory && (
                  <Collapsible open={isOpen} onOpenChange={() => togglePrice(price.id)}>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-1">
                      <History className="w-4 h-4" />
                      <span>Se prishistorik ({history.length} {history.length === 1 ? 'ændring' : 'ændringer'})</span>
                      <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="relative pl-4 border-l-2 border-border ml-2 space-y-3">
                        {history
                          .sort((a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime())
                          .map((change, index) => {
                            const diff = change.new_price_dkk - change.old_price_dkk;
                            const isDecrease = diff < 0;
                            
                            return (
                              <div 
                                key={change.id} 
                                className="relative"
                              >
                                {/* Timeline dot */}
                                <div 
                                  className={`absolute -left-[calc(0.5rem+1px)] top-1.5 w-2 h-2 rounded-full ${
                                    isDecrease ? 'bg-green-500' : 'bg-destructive'
                                  }`} 
                                />
                                
                                <div className="text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">
                                      {getRelativeTime(change.changed_at)}
                                    </span>
                                    <span className={`font-medium ${isDecrease ? 'text-green-600' : 'text-destructive'}`}>
                                      {isDecrease ? '↓' : '↑'} {formatPrice(Math.abs(diff))}
                                    </span>
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {formatPrice(change.old_price_dkk)} → {formatPrice(change.new_price_dkk)}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        
                        {/* Initial price marker */}
                        <div className="relative">
                          <div className="absolute -left-[calc(0.5rem+1px)] top-1.5 w-2 h-2 rounded-full bg-primary" />
                          <div className="text-sm">
                            <span className="text-muted-foreground">Lanceret</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              til {formatPrice(history[history.length - 1]?.old_price_dkk || price.price_dkk)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
